"use client";

import { useEffect, useState } from "react";
import { useLanguage } from "../../app/components/context/LanguageContext";
import { useTheme } from "../../app/context/ThemeContext";
import Link from "next/link";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";

/* ===================== TYPES ===================== */

interface FeaturedImage {
  url: string;
  alternativeText?: string;
}

interface SummaryParagraph {
  type: string;
  children: { text: string }[];
}

interface NewsItem {
  id: number;
  title: string;
  slug: string;
  summary: SummaryParagraph[];
  featuredImage: FeaturedImage[];
  category?: string;
  author?: string;
}

interface Document {
  name: string;
  url: string;
}

interface Location {
  LocationName: string;
}

interface Tender {
  id: number;
  IDs?: number;
  title: string;
  slug: string;
  modedepassation: string;
  typeofreview?: string | null;
  DatedeParution?: string | null;
  DateLimitedeSoumission?: string | null;
  Documents?: Document | null;
  LieuDeRetraitDuCahierDesCharges?: Location | null;
  LieudeDepotdesOffres?: Location | null;
}

const translations: any = {
  fr: {
    label1: "Accueil",
    label2: "Appels d’offres",
    Loading: "Chargement ...",
    Expiré: "Expiré",
    Actif: "Actif",
    Publication: "Date de publication",
    Date_limite: "Date limite",
    Document_disponible: "Document disponible",
    Retrait: "Retrait du dossier",
    Dépôt: "Dépôt des offres",
    Voir_les_détails: "Voir les détails",
    Reference: "Réf.",
  },

  en: {
    label1: "Home",
    label2: "Public tenders",
    Loading: "Loading ...",
    Expiré: "Expired",
    Actif: "Active",
    Publication: "Publication date",
    Date_limite: "Deadline",
    Document_disponible: "Document available",
    Retrait: "File withdrawal",
    Dépôt: "Bid submission",
    Voir_les_détails: "View details",
    Reference: "Ref.",
  },

  ar: {
    label1: "الرئيسية",
    label2: "المناقصات العمومية",
    Loading: "جارٍ التحميل ...",
    Expiré: "منتهي الصلاحية",
    Actif: "نشط",
    Publication: "تاريخ النشر",
    Date_limite: "آخر أجل",
    Document_disponible: "الوثائق متوفرة",
    Retrait: "سحب الملف",
    Dépôt: "إيداع العروض",
    Voir_les_détails: "عرض التفاصيل",
    Reference: "المرجع",
  },
};

/* ===================== COMPONENT ===================== */

export default function NewsAndPublicTendersSection() {
  const { language } = useLanguage();
  const { theme } = useTheme();

  const [news, setNews] = useState<NewsItem[]>([]);
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [loading, setLoading] = useState(true);
  const t = translations[language] || translations.en;

  const isDark = theme === "dark";

  /* ===================== FETCH NEWS ===================== */
  useEffect(() => {
    async function fetchNews() {
      const newsRes = await fetch(
        `${process.env.NEXT_PUBLIC_STRAPI_URL}/api/newsses?populate=*`
      );
      const data = await newsRes.json();
      setNews(data.data || []);
    }
    fetchNews();
  }, [language]);

  /* ===================== FETCH TENDERS ===================== */
  useEffect(() => {
    let isMounted = true;

    async function fetchTenders() {
      try {
        const res = await fetch(`/api/publictenders?locale=${language}`);
        const data = await res.json();

        if (isMounted) setTenders(data.data || []);
      } catch (err) {
        console.error("Impossible de charger les appels d'offres:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchTenders();
    return () => {
      isMounted = false;
    };
  }, [language]);

  const lastSixTenders = [...tenders].sort((a, b) => b.id - a.id).slice(0, 6);

  return (
    <section className={`py-24 ${isDark ? "bg-gray-950" : "bg-white"}`}>
      <div className="max-w-7xl mx-auto px-4 space-y-32">
        
        {/* ===================== NEWS ===================== */}
        <div>
          <h2
            className={`text-4xl font-bold text-center mb-12 ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            {language === "fr" && "Actualités Récentes"}
            {language === "en" && "Recent News"}
            {language === "ar" && "آخر الأخبار"}
          </h2>

          <Swiper
            modules={[Autoplay]}
            loop
            autoplay={{ delay: 3000, disableOnInteraction: false }}
            spaceBetween={30}
            breakpoints={{
              640: { slidesPerView: 1 },
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 4 },
            }}
          >
            {news.map((item) => {
              const imageUrl = item.featuredImage?.[0]?.url
                ? `${process.env.NEXT_PUBLIC_STRAPI_URL}${item.featuredImage[0].url}`
                : "/placeholder.jpg";

              const summary =
                item.summary
                  ?.map((p) => p.children.map((c) => c.text).join(""))
                  .join(" ")
                  .substring(0, 100) + "...";

              return (
                <SwiperSlide key={item.id} className="!h-auto">
                  <Link href={`/news/${item.slug}`} className="block group h-full">
                    <article className="flex flex-col h-full">
                      <div className="w-full aspect-[16/9] overflow-hidden bg-gray-100 dark:bg-gray-800">
                        <img
                          src={imageUrl}
                          alt={item.title}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>

                      <div className="pt-5 flex flex-col flex-1">
                        <h3
                          className={`
                            text-xl font-bold leading-snug mb-3 line-clamp-2
                            ${isDark ? "text-white" : "text-gray-900"}
                            group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors
                          `}
                        >
                          {item.title}
                        </h3>

                        <p
                          className={`
                            text-sm leading-relaxed line-clamp-2 mb-4
                            ${isDark ? "text-gray-400" : "text-gray-500"}
                          `}
                        >
                          {summary}
                        </p>

                        <div className="mt-auto">
                          <span
                            className={`
                              text-xs font-medium uppercase tracking-wider
                              ${isDark ? "text-gray-500" : "text-gray-400"}
                            `}
                          >
                            {item.author || item.category || "News"}
                          </span>
                        </div>
                      </div>
                    </article>
                  </Link>
                </SwiperSlide>
              );
            })}
          </Swiper>

          <div className="text-center mt-12">
            <Link href="/news">
              <button
                className={`px-6 py-3 rounded-xl font-semibold ${
                  isDark ? "bg-blue-600 text-white" : "bg-gray-900 text-white"
                }`}
              >
                {language === "fr" && "Voir plus news →"}
                {language === "en" && "See more news →"}
                {language === "ar" && "عرض المزيد من الأخبار ←"}
              </button>
            </Link>
          </div>
        </div>

  

        {/* ===================== PUBLIC TENDERS ===================== */}
        <div>
          <h2
            className={`text-4xl font-bold text-center mb-12 ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            {language === "fr" && "Derniers appels d’offres"}
            {language === "en" && "Latest Public Tenders"}
            {language === "ar" && "أحدث المناقصات"}
          </h2>

          <Swiper
            modules={[Autoplay]}
            loop
            autoplay={{ delay: 3500, disableOnInteraction: false }}
            spaceBetween={30}
            breakpoints={{
              640: { slidesPerView: 1 },
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
          >
            {lastSixTenders.map((tender) => (
              <SwiperSlide key={tender.id} className="!h-auto">
                <TenderCard tender={tender} theme={theme} t={t} />
              </SwiperSlide>
            ))}
          </Swiper>

          <div className="text-center mt-12">
            <Link href="/publictenders">
              <button
                className={`px-6 py-3 rounded-xl font-semibold ${
                  isDark ? "bg-blue-600 text-white" : "bg-gray-900 text-white"
                }`}
              >
                {language === "fr" && "Voir plus d’appels d’offres →"}
                {language === "en" && "See more tenders →"}
                {language === "ar" && "عرض المزيد من المناقصات ←"}
              </button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ===================== TENDER CARD (NOW WHITE, NO GREEN) ===================== */

function TenderCard({
  tender,
  theme,
  t,
}: {
  tender: Tender;
  theme: string;
  t: any;
}) {
  const isDark = theme === "dark";

  const isExpired = tender.DateLimitedeSoumission
    ? new Date(tender.DateLimitedeSoumission) < new Date()
    : false;

  // BACKGROUND CHANGED TO WHITE (was beige/paper)
  const cardBg = isDark ? "bg-[#1F2937]" : "bg-white";
  const innerBg = isDark ? "bg-[#111827]" : "bg-white";
  const titleColor = isDark ? "text-white" : "text-gray-900";
  const subtitleColor = isDark ? "text-gray-400" : "text-gray-500";
  const lineColor = isDark ? "border-gray-700" : "border-gray-200";
  const labelColor = isDark ? "text-gray-500" : "text-gray-500";
  const valueColor = isDark ? "text-gray-200" : "text-gray-800";

  return (
    <Link href={`/publictenders/${tender.slug}`} className="block h-full">
      <article
        className={`
          ${cardBg}
          rounded-3xl
          overflow-hidden
          h-[520px]
          md:h-[600px]
          flex flex-col
          transition-all duration-500
          hover:-translate-y-1
          hover:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)]
          border
          ${isDark ? "border-gray-800" : "border-gray-200"}
        `}
      >
        {/* ========== TOP ========== */}
        <header className="pt-8 pb-6 px-6 text-center">
          <span
            className={`inline-block text-[11px] font-semibold uppercase tracking-[0.22em] mb-3 ${subtitleColor}`}
          >
            {t.Reference} #{tender.IDs || tender.id}
          </span>

          <h3
            className={`
              text-2xl md:text-[26px] font-bold leading-tight tracking-tight mb-2
              ${titleColor}
            `}
            style={{ textWrap: "balance" }}
          >
            {tender.modedepassation || tender.title}
          </h3>

          <p className={`text-[13px] md:text-sm font-normal ${subtitleColor}`}>
            {tender.typeofreview || "Avis de publication"}
          </p>
        </header>

        {/* ========== INNER PANEL ========== */}
        <div className="px-5 pb-5 flex-1 flex min-h-0">
          <div
            className={`
              ${innerBg}
              w-full
              rounded-[22px]
              border
              ${isDark ? "border-gray-800" : "border-gray-200"}
              p-5
              flex flex-col
              gap-3
              overflow-hidden
            `}
          >
            {/* Status pill - NEUTRAL (no green) */}
            <div className="flex items-center justify-center">
              <span
                className={`
                  inline-flex items-center gap-1.5
                  text-[10px] font-semibold uppercase tracking-[0.18em]
                  px-3 py-1 rounded-full
                  ${
                    isExpired
                      ? isDark
                        ? "bg-red-500/15 text-red-300"
                        : "bg-red-100 text-red-700"
                      : isDark
                      ? "bg-gray-800 text-gray-300"
                      : "bg-gray-100 text-gray-700"
                  }
                `}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    isExpired ? "bg-red-500" : "bg-gray-400 animate-pulse"
                  }`}
                />
                {isExpired ? t.Expiré : t.Actif}
              </span>
            </div>

            {/* Title small */}
            <p
              className={`
                text-center text-[13px] leading-snug line-clamp-2
                ${isDark ? "text-gray-300" : "text-gray-700"}
              `}
            >
              {tender.title}
            </p>

            {/* Divider */}
            <div className={`h-px w-full ${lineColor} border-t`} />

            {/* Dates & locations */}
            <dl className="space-y-2.5 text-[12.5px]">
              {tender.DatedeParution && (
                <Row
                  label={t.Publication}
                  value={tender.DatedeParution}
                  labelColor={labelColor}
                  valueColor={valueColor}
                />
              )}

              {tender.DateLimitedeSoumission && (
                <Row
                  label={t.Date_limite}
                  value={tender.DateLimitedeSoumission}
                  labelColor={labelColor}
                  valueColor={
                    isExpired ? "text-red-500 font-semibold" : valueColor
                  }
                />
              )}

              {tender.LieuDeRetraitDuCahierDesCharges?.LocationName && (
                <Row
                  label={t.Retrait}
                  value={tender.LieuDeRetraitDuCahierDesCharges.LocationName}
                  labelColor={labelColor}
                  valueColor={valueColor}
                />
              )}

              {tender.LieudeDepotdesOffres?.LocationName && (
                <Row
                  label={t.Dépôt}
                  value={tender.LieudeDepotdesOffres.LocationName}
                  labelColor={labelColor}
                  valueColor={valueColor}
                />
              )}
            </dl>

            {/* Document chip */}
            {tender.Documents && (
              <div
                className={`
                  mt-auto
                  flex items-center justify-center gap-2
                  text-[11px] font-semibold uppercase tracking-wider
                  px-3 py-2 rounded-lg
                  ${
                    isDark
                      ? "bg-blue-500/10 text-blue-300 border border-blue-500/20"
                      : "bg-blue-50 text-blue-700 border border-blue-100"
                  }
                `}
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                {t.Document_disponible}
              </div>
            )}
          </div>
        </div>

        {/* ========== BOTTOM ========== */}
        <div
          className={`
            px-6 py-4
            border-t
            ${lineColor}
            flex items-center justify-between
          `}
        >
          <span
            className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${
              isExpired ? "text-red-500" : "text-gray-600 dark:text-gray-400"
            }`}
          >
            {isExpired ? t.Expiré : t.Actif}
          </span>

          <span
            className={`
              inline-flex items-center gap-1.5
              text-[13px] font-semibold
              transition-transform duration-300
              group-hover:translate-x-0.5
              ${isDark ? "text-white" : "text-gray-900"}
            `}
          >
            {t.Voir_les_détails}
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.4}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </span>
        </div>
      </article>
    </Link>
  );
}

/* ========== Small row helper ========== */
function Row({
  label,
  value,
  labelColor,
  valueColor,
}: {
  label: string;
  value: string;
  labelColor: string;
  valueColor: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt
        className={`text-[10.5px] font-semibold uppercase tracking-[0.14em] ${labelColor} shrink-0`}
      >
        {label}
      </dt>
      <dd className={`text-end ${valueColor} font-medium`}>{value}</dd>
    </div>
  );
}